import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { takedownApi } from "core/api";
import { ReplyRecord } from "core/types/takedown.types";
import { categoryMeta } from "./replyCategories";

const fmtDate = (raw?: string | null): string => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

/**
 * Inbox globale risposte legal@codertd.com — GET /admin/takedown/replies (senza practice_id).
 * Complementa la sezione "Azioni richieste" per-pratica già presente in takedown.tsx: qui si
 * vede TUTTO (anche removed_confirmed/ack_ticket/auto_reply), non solo needs_human.
 */
export const RepliesInboxPage: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ReplyRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [onlyNeedsHuman, setOnlyNeedsHuman] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { replies } = await takedownApi.getReplies();
      setRows(Array.isArray(replies) ? replies : []);
    } catch (e: any) {
      setError(t("takedown.replies.error_load", { error: e?.message || e }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(
    () => (onlyNeedsHuman ? rows.filter((r) => r.needs_human) : rows),
    [rows, onlyNeedsHuman]
  );

  const needsHumanCount = useMemo(() => rows.filter((r) => r.needs_human).length, [rows]);

  return (
    <Paper sx={{ borderRadius: "10px", p: "20px", mb: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <MarkEmailUnreadOutlinedIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {t("takedown.replies.title")}
          </Typography>
          {needsHumanCount > 0 && (
            <Chip size="small" color="warning" label={t("takedown.replies.needs_review_count", { count: needsHumanCount })} />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={<Switch size="small" checked={onlyNeedsHuman} onChange={(e) => setOnlyNeedsHuman(e.target.checked)} />}
            label={t("takedown.replies.only_needs_action")}
          />
          <Tooltip title={t("takedown.common.actions.refresh")}>
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={40} color="secondary" />
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 480, mt: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("takedown.replies.table.domain")}</TableCell>
                <TableCell>{t("takedown.replies.table.category")}</TableCell>
                <TableCell>{t("takedown.replies.table.sender")}</TableCell>
                <TableCell>{t("takedown.replies.table.subject")}</TableCell>
                <TableCell>{t("takedown.replies.table.todo")}</TableCell>
                <TableCell>{t("takedown.replies.table.ticket")}</TableCell>
                <TableCell>{t("takedown.replies.table.received")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const cm = categoryMeta(r.category, t);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.domain || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={cm.color} label={cm.label} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.from_addr || "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.subject || "—"}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary">
                        {r.todo}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.ticket_ref || "—"}</TableCell>
                    <TableCell>{fmtDate(r.received_at)}</TableCell>
                  </TableRow>
                );
              })}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                      {onlyNeedsHuman ? t("takedown.replies.empty_filtered") : t("takedown.replies.empty_all")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default RepliesInboxPage;
